import { action } from "mobx";
import BaseStore from "../base";
import { localStore } from "@app/lib/storage";
import sessionStore from "../session";

/**
 * Simple hash function for content
 * @param {string} str
 * @returns {string}
 */
function simpleHash(str) {
    let hash = 0;
    if (str.length === 0) {
        return hash.toString();
    }

    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
}

/**
 * Store for managing cursor position and scroll state across editor sessions
 */
export class CursorPositionStore extends BaseStore {
    /**
     * Observable store data
     */
    observable() {
        return {
            currentDocument: null,
            currentSection: null,
        };
    }

    /**
     * Get storage key for cursor position
     * Includes userId to ensure each user has their own cursor position
     * @param {string} projectId
     * @param {string} sectionId
     * @returns {string}
     */
    getStorageKey(projectId, sectionId) {
        const userId = sessionStore.user?._id || "anonymous";
        return `cursor:${userId}:${projectId}::${sectionId}`;
    }

    /**
     * Get storage key for current active document/section
     * Includes userId to ensure each user has their own active document state
     * @returns {string}
     */
    getCurrentDocumentKey() {
        const userId = sessionStore.user?._id || "anonymous";
        return `editor.cursor.current:${userId}`;
    }

    /**
     * Save cursor position for a document section
     * @param {string} projectId
     * @param {string} sectionId
     * @param {number} cursorFrom
     * @param {number} cursorTo
     * @param {number} scrollY
     * @param {string|object} content - Content to hash for change detection
     */
    @action
    saveCursorPosition(projectId, sectionId, cursorFrom, cursorTo, scrollY, content) {
        if (!projectId || !sectionId) {
            return;
        }

        const contentStr = typeof content === "string" ? content : JSON.stringify(content);
        const contentHash = simpleHash(contentStr);

        const position = {
            projectId,
            sectionId,
            cursorFrom,
            cursorTo,
            scrollY,
            timestamp: Date.now(),
            contentHash,
        };

        const key = this.getStorageKey(projectId, sectionId);
        localStore.set(key, position);
    }

    /**
     * Load cursor position for a document section
     * @param {string} projectId
     * @param {string} sectionId
     * @returns {object|null}
     */
    loadCursorPosition(projectId, sectionId) {
        if (!projectId || !sectionId) {
            return null;
        }

        const key = this.getStorageKey(projectId, sectionId);
        return localStore.get(key, null);
    }

    /**
     * Clear cursor position for a document section
     * @param {string} projectId
     * @param {string} sectionId
     */
    @action
    clearCursorPosition(projectId, sectionId) {
        if (!projectId || !sectionId) {
            return;
        }

        const key = this.getStorageKey(projectId, sectionId);
        localStore.remove(key);
    }

    /**
     * Clear all cursor positions for a specific project (for current user only)
     * @param {string} projectId - Project ID to clear positions for
     */
    @action
    clearProjectCursorPositions(projectId) {
        if (!projectId) {
            return;
        }

        try {
            const userId = sessionStore.user?._id || "anonymous";
            const keys = Object.keys(localStorage);
            const prefix = `vs:cursor:${userId}:${projectId}::`;
            keys.forEach((key) => {
                if (key.includes(prefix)) {
                    localStorage.removeItem(key);
                }
            });
        } catch (e) {
            console.warn("Failed to clear project cursor positions:", e);
        }
    }

    /**
     * Clear all cursor positions (for current user only)
     */
    @action
    clearAllCursorPositions() {
        try {
            const userId = sessionStore.user?._id || "anonymous";
            const keys = Object.keys(localStorage);
            keys.forEach((key) => {
                if (key.includes(`vs:cursor:${userId}:`)) {
                    localStorage.removeItem(key);
                }
            });
        } catch (e) {
            console.warn("Failed to clear all cursor positions:", e);
        }
    }

    /**
     * Save current active document/section
     * @param {string} documentId
     * @param {string} sectionId
     * @param {string} route - Current route path
     */
    @action
    saveCurrentDocument(documentId, sectionId, route) {
        if (!documentId || !sectionId) {
            return;
        }

        const current = {
            documentId,
            sectionId,
            route,
            timestamp: Date.now(),
        };

        this.currentDocument = documentId;
        this.currentSection = sectionId;

        localStore.set(this.getCurrentDocumentKey(), current);
    }

    /**
     * Load current active document/section
     * @returns {object|null}
     */
    loadCurrentDocument() {
        const current = localStore.get(this.getCurrentDocumentKey(), null);

        if (current) {
            this.currentDocument = current.documentId;
            this.currentSection = current.sectionId;
        }

        return current;
    }

    /**
     * Clear current active document/section
     */
    @action
    clearCurrentDocument() {
        this.currentDocument = null;
        this.currentSection = null;
        localStore.remove(this.getCurrentDocumentKey());
    }

    /**
     * Check if content has changed significantly
     * @param {string|object} currentContent
     * @param {string} savedHash
     * @returns {boolean}
     */
    hasContentChanged(currentContent, savedHash) {
        if (!savedHash) {
            return false;
        }

        const contentStr =
            typeof currentContent === "string" ? currentContent : JSON.stringify(currentContent);
        const currentHash = simpleHash(contentStr);
        return currentHash !== savedHash;
    }

    /**
     * Get storage key for force release flag
     * Includes userId to ensure each user has their own force release state
     * @param {string} sectionId
     * @returns {string}
     */
    getForceReleaseKey(sectionId) {
        const userId = sessionStore.user?._id || "anonymous";
        return `forceRelease:${userId}:${sectionId}`;
    }

    /**
     * Mark a section as force-released
     * @param {string} sectionId
     */
    @action
    markForceReleased(sectionId) {
        if (!sectionId) {
            return;
        }

        const key = this.getForceReleaseKey(sectionId);
        localStore.set(key, { timestamp: Date.now() });
    }

    /**
     * Check if a section was force-released
     * @param {string} sectionId
     * @returns {boolean}
     */
    isForceReleased(sectionId) {
        if (!sectionId) {
            return false;
        }

        const key = this.getForceReleaseKey(sectionId);
        return localStore.get(key, null) !== null;
    }

    /**
     * Clear force release flag for a section
     * @param {string} sectionId
     */
    @action
    clearForceReleased(sectionId) {
        if (!sectionId) {
            return;
        }

        const key = this.getForceReleaseKey(sectionId);
        localStore.remove(key);
    }
}

const cursorPositionStore = new CursorPositionStore();
export default cursorPositionStore;
